/**
 * The server-authorization token's shared vocabulary: audience, lifetime, and
 * scope set. platform-api mints against these constants and the local server
 * verifies against them, so they live in one place rather than as matching
 * string literals in two projects that fail open when they drift.
 *
 * This package is the home for the same reason `PROTOCOL_VERSION` and
 * `CODEX_CLI_VERSION` landed here: it is product-authored configuration that
 * a service and a client both read, and it has a `build` target, so it is a
 * legal TypeScript project-reference target for both consumers.
 *
 * Two properties are load-bearing and must not be "simplified":
 *
 * 1. The token carries `role` and `scopes` but deliberately **no entitlement
 *    state**. Server authorization and inference entitlement are separately
 *    revocable and must report differently — a signed-in but unentitled user is
 *    a supported state, not an error.
 * 2. The read scope is distinct from the operate scopes. Collapsing the set to
 *    a boolean would satisfy the type checker and let a read grant operate.
 */
/**
 * `aud` on every server-authorization token. A token minted for anything else
 * must be rejected by the server's verifier even when the signature is valid,
 * which is what stops a token issued for a different consumer of the same
 * JWKS from authorizing server operations.
 */
export const SERVER_TOKEN_AUDIENCE = "dovod-server";
/** Gateway session-liveness check authenticated by a server token. */
export const SERVER_SESSION_PROBE_PATH = "/api/server-session";
/**
 * Token lifetime, pinned rather than inherited. better-auth@1.6.23's `jwt`
 * plugin defaults `expirationTime` to `15m`, which is the product's value
 * for the desktop token — but a default is not a decision. Pinning it means an
 * upgrade that changes the plugin default cannot silently move the product's
 * outer revocation bound.
 *
 * Expressed twice on purpose: the `ms`-style string is what the plugin option
 * takes, the seconds are what a verifier and a test compare against.
 */
export const SERVER_TOKEN_EXPIRATION = "15m";
export const SERVER_TOKEN_EXPIRATION_SECONDS = 900;
/**
 * How often the renderer mints a replacement. Comfortably inside the 15-minute
 * expiry so a single missed refresh does not end the session.
 */
export const SERVER_TOKEN_REFRESH_INTERVAL_SECONDS = 12 * 60;
/**
 * How often an open connection re-checks the authorization it was handed at
 * handshake time. It stays within 30-60s; revocation on a live socket is
 * unobservable without it, and the failure is silent — nothing errors and the
 * behaviour looks correct.
 */
export const SERVER_LIVENESS_SWEEP_INTERVAL_SECONDS = 30;
/**
 * The scope vocabulary a token may carry. The read-versus-operate distinction is
 * what keeps a read grant from operating.
 */
export const SERVER_SCOPES = [
    "orchestration:read",
    "orchestration:operate",
    "terminal:operate",
    "review:write",
];
/**
 * Scopes a token may still carry and nothing mints. `terminal:operate` gated the
 * terminal, which left the product; a token minted before an upgrade keeps
 * verifying for the rest of its 15 minutes because the vocabulary still names it.
 */
export const RETIRED_SERVER_SCOPES = ["terminal:operate"];
/** The one scope that reads without changing state. */
export const SERVER_READ_SCOPE = "orchestration:read";
/**
 * What a signed-in Dovod user's server token carries. Every scope that is not
 * retired, because the server acts only on its own owner's workspace — the
 * distinction exists so a *narrower* token is enforceable, not because normal
 * sign-in issues one.
 */
export const DEFAULT_SERVER_SCOPES = SERVER_SCOPES.filter((scope) => !RETIRED_SERVER_SCOPES.includes(scope));
/**
 * What an operator's read-only entry into a team's web workspace grants: the
 * scopes its server token carries, and the unsafe gateway requests (`<METHOD>
 * <path>`) its session may make. Every other non-GET/HEAD/OPTIONS request of
 * that session answers 403. Widening the entry's rights is a change to these
 * two lists.
 */
export const OPERATOR_ENTRY_SCOPES = [SERVER_READ_SCOPE];
export const OPERATOR_ENTRY_UNSAFE_ALLOWED = ["POST /api/auth/sign-out"];
export function isServerScope(value) {
    return typeof value === "string" && SERVER_SCOPES.includes(value);
}
/** Server-token roles. Mirrors platform-api's `user`/`operator` pair. */
export const SERVER_ROLES = ["user", "operator"];
export function isServerRole(value) {
    return typeof value === "string" && SERVER_ROLES.includes(value);
}
/**
 * Where a server token admits its account: `desktop` is the local server, `web`
 * the team server of the account's organisation. The token carries the
 * account's set as `platforms`, and each server admits only a token that names
 * its own.
 */
export const SERVER_PLATFORMS = ["desktop", "web"];
export function isServerPlatform(value) {
    return typeof value === "string" && SERVER_PLATFORMS.includes(value);
}
